import type { VisualRole } from "@eldrun/content";
import type { ShaderMaterial } from "three";
import type { AtmosphereUniforms } from "../atmosphere.ts";
import { createSurfaceMaterial, surfaceVariant } from "./surface.ts";

/** Classe do material na máscara do contorno e no rim noturno (F51). */
export interface ToonLook {
  /** Personagens, itens e estruturas: alfa 0 na máscara do contorno (F52). */
  outline?: boolean;
  /** Assombrações: rim frio que cresce com a noite. */
  nightRim?: boolean;
  /** Papel na malha neon (F59): o que a peça é (padrão `estrutura`). */
  role?: VisualRole;
  /** Cor do dono na malha neon (F60; padrão, o neutro). */
  accent?: string;
}

/**
 * Materiais por atmosfera e por cor e classe. A chave inclui a atmosfera: cada partida (revanche)
 * monta a sua, e um material guardado da anterior leria uniforms que ninguém mais atualiza.
 */
const caches = new WeakMap<AtmosphereUniforms, Map<string, ShaderMaterial>>();

/**
 * Material toon facetado (personagens, criaturas, objetos) com cor sólida por parte: o material de
 * superfície sem cor por vértice (F51). Um material por cor e classe, compartilhado: o mesmo tom em
 * vários personagens não cria material novo, e cor, rim, brilho e classe são uniforms (um programa só).
 */
export function toonMaterial(atmosphere: AtmosphereUniforms, hex: string, rim = 1, glow = false, look: ToonLook = {}): ShaderMaterial {
  const outline = look.outline === true;
  const nightRim = look.nightRim === true;
  const role = look.role ?? "estrutura";
  const key = `${hex}:${rim}:${glow}:${outline}:${nightRim}:${role}:${look.accent ?? ""}`;
  let cache = caches.get(atmosphere);
  if (!cache) caches.set(atmosphere, (cache = new Map()));
  let material = cache.get(key);
  if (!material) {
    material = createSurfaceMaterial({ atmosphere, name: "toon", color: hex, rim, glow, outline, nightRim, role, ...(look.accent !== undefined && { accent: look.accent }) });
    cache.set(key, material);
  }
  return material;
}

/**
 * Dissolve (F21): o corpo de quem morre some em pedacinhos com a borda acesa, e volta do mesmo
 * jeito ao reviver. Variante do toon com `uDissolve` (0 inteiro, 1 sumido), uma por material de
 * cada corpo que some; o descarte vem depois da luz (das derivadas). A borda (`uDissolveEdge`, F95) é o
 * próprio uniform do acento do material: segue a cor do dono como antes.
 */
export function toonDissolveMaterial(base: ShaderMaterial): ShaderMaterial {
  return surfaceVariant(base, { name: "toon-dissolve", defines: { USE_DISSOLVE: "" }, uniforms: { uDissolve: { value: 0 }, uDissolveEdge: base.uniforms["uStyleAccent"]! } });
}
